/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CidModifiersPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    CID Modifiers list page with table view, search, pagination, and actions.
    Allows viewing, creating, editing, and deleting CID modifiers.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, direction, action type, priority, and status

 ?? Last Updated: 2025-01-28 by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Copy, Edit, Trash2, Phone } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { deleteCidModifierById, navigateToCidModifierDuplicate } from './cidModifiersActions';
import { useCidModifierList } from './hooks/useCidModifierList';
import { CidModifier } from './types';

export default function CidModifiersPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  // Set page title
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Delete confirmation state
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; modifier: CidModifier | null }>({
    isOpen: false,
    modifier: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useCidModifierList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: CidModifier) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }
        if (column.key === 'source') {
          return {
            ...column,
            render: (_value: unknown, item: CidModifier) => (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                item.source === 'static'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  : item.source === 'database'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
              }`}>
                {t(`cidModifiers.sources.${item.source}`, item.source)}
              </span>
            ),
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: CidModifier) => (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                item.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              }`}>
                {item.enabled ? t('common.enabled') : t('common.disabled')}
              </span>
            ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: CidModifier) => {
              const date = new Date(item.createdAt);
              const isValidDate = !isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValidDate ? date.toLocaleDateString() : 'N/A'}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  // Handlers
  const handleCreate = () => {
    navigate('/pbx/incoming-tools/cid-modifiers/new');
  };

  const handleEdit = (modifier: CidModifier) => {
    navigate(`/pbx/incoming-tools/cid-modifiers/${modifier.uuid ?? modifier.id}`);
  };

  const handleDuplicate = useCallback((modifier: CidModifier) => {
    navigateToCidModifierDuplicate(navigate, modifier.uuid ?? modifier.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((modifier: CidModifier) => {
    setDeleteModal({ isOpen: true, modifier });
  }, []);

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.modifier) return;

    setIsDeleting(true);
    try {
      await deleteCidModifierById(deleteModal.modifier.id);
      showSuccess(t('cidModifiers.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, modifier: null });
      refresh();
    } catch (error) {
      showError(error.message || t('cidModifiers.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.modifier, showError, showSuccess, t, refresh]);

  // Render row actions
  const renderRowActions = useCallback((modifier: CidModifier) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common:edit')} position="top">
        <button
          type="button"
          onClick={() => handleEdit(modifier)}
          aria-label={t('common:edit')}
          className="p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 hover:bg-blue-100 dark:hover:bg-blue-900/30"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common:duplicate')} position="top">
        <button
          type="button"
          onClick={() => handleDuplicate(modifier)}
          aria-label={t('common:duplicate')}
          className="p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common:delete')} position="top">
        <button
          type="button"
          onClick={() => handleDeleteClick(modifier)}
          aria-label={t('common:delete')}
          className="p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 hover:bg-red-100 dark:hover:bg-red-900/30"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleEdit, handleDuplicate, handleDeleteClick, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('cidModifiers.title')}
          titleIcon={<Phone className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('cidModifiers.searchPlaceholder')}
          renderActions={renderRowActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, modifier: null })}
        onConfirm={handleDeleteConfirm}
        title={t('cidModifiers.messages.deleteTitle') || 'Delete CID Modifier'}
        message={t('cidModifiers.messages.confirmDelete', { name: deleteModal.modifier?.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

